// Pure helpers for the in-app evidence camera. There is no file picker and no
// gallery path: a photo is a frame grabbed from a live getUserMedia stream.
// Every browser global is injectable so jsdom tests can stub canvas, crypto and
// mediaDevices.

// ponytail: long-edge cap; the server refuses width > 2048 or height > 2048 + band.
export const MAX_EDGE = 2048;
// ponytail: JPEG quality knob; about 300-600 KB for a 1080p frame.
export const JPEG_QUALITY = 0.85;

// Must equal bandHeight in server/installer-system/photoHash.js: the server crops
// exactly this many rows off the bottom before the near-duplicate hash.
export const bandHeight = (width) => Math.max(72, Math.round(width * 0.09));

const newCanvas = () => document.createElement('canvas');

// Video-frame grab at the stream's native size (no ImageCapture.takePhoto).
export function grabFrame(video, createCanvas = newCanvas) {
  const canvas = createCanvas();
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}

const pad = (n) => String(n).padStart(2, '0');
const stampTime = (ms) => {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// The two band lines (contract C7). Never coordinates: the AI copy of the photo
// goes to OpenRouter or Codex, which must not learn the customer's location.
export function bandLines({ jobCode, at, accuracy_m, distance_m }) {
  const metres = (v) => (Number.isFinite(v) ? String(Math.round(v)) : '-');
  return [`JOB ${String(jobCode ?? '').slice(0, 6)}  ${stampTime(at)}`, `±${metres(accuracy_m)} m  ${metres(distance_m)} m`];
}

// Downscale to MAX_EDGE (never upscale), append the band when stamp, encode JPEG.
export function renderEvidence(source, { stamp = true, lines = [], createCanvas = newCanvas } = {}) {
  const scale = Math.min(1, MAX_EDGE / Math.max(source.width, source.height));
  const width = Math.round(source.width * scale);
  const scene = Math.round(source.height * scale);
  const band = stamp ? bandHeight(width) : 0;
  const canvas = createCanvas();
  canvas.width = width;
  canvas.height = scene + band;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(source, 0, 0, width, scene);
  if (stamp) {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, scene, width, band);
    ctx.fillStyle = '#fff';
    ctx.font = `bold ${Math.round(band * 0.3)}px monospace`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    lines.slice(0, 2).forEach((text, i) => ctx.fillText(text, Math.round(band * 0.2), scene + band * (i ? 0.72 : 0.3)));
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve({ blob, width, height: scene + band }) : reject(new Error('JPEG encode failed'))), 'image/jpeg', JPEG_QUALITY);
  });
}

const blobBytes = (blob) => (typeof blob.arrayBuffer === 'function' ? blob.arrayBuffer() : new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error);
  reader.readAsArrayBuffer(blob);
}));

// Lowercase hex SHA-256 of the exact blob bytes the server will later re-hash.
export async function sha256Hex(blob, subtle = globalThis.crypto?.subtle) {
  const digest = await subtle.digest('SHA-256', new Uint8Array(await blobBytes(blob)));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

// Cheap, forgeable signals; they catch desktop webcams, OBS and naive injection.
export function cameraSignals(track, mediaDevices = globalThis.navigator?.mediaDevices) {
  const getUserMedia = mediaDevices?.getUserMedia;
  return {
    facing: track?.getSettings?.().facingMode ?? null,
    label: Boolean(track?.label),
    native: typeof getUserMedia === 'function' && /\[native code\]/.test(Function.prototype.toString.call(getUserMedia)),
    canvas: Boolean(track) && 'canvas' in track,
  };
}

export const inAppBrowser = (ua = globalThis.navigator?.userAgent || '') => /; wv\)|Telegram|Instagram|FBAN|FBAV|Eitaa|Rubika|Bale/i.test(ua);

// iPadOS reports a Mac user agent; touch points tell them apart.
export const isIOS = (ua = globalThis.navigator?.userAgent || '', touchPoints = globalThis.navigator?.maxTouchPoints || 0) =>
  /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
